import { css, html, nothing, svg, type TemplateResult } from "lit";
import { readUrlParams } from "./wire";
import { askToReportIssue } from "./report-issue";

// The tab bar every page but the table opens with, as on Arena: icon over label, the open tab
// underlined in the playtester's orange.

export type TopNavTab = "home" | "decks" | "profile" | "settings" | "admin";

export type TopNav = {
  // Null on a page no tab names, a room's say.
  open: TopNavTab | null;
  displayName: string | null;
  // People on the site, from the room list (platform/game's /rooms); null or absent until it loads.
  online?: number | null;
  links: Record<TopNavTab, string>;
  // Shows the Admin tab; the Worker still checks ADMIN_USER_IDS on every admin call.
  admin?: boolean;
};

const ICONS: Record<TopNavTab, TemplateResult> = {
  home: html`<svg viewBox="0 0 24 24" aria-hidden="true">${svg`<path d="M3 11.5 12 4l9 7.5M5.5 9.5V20h5v-6h3v6h5V9.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"></path>`}</svg>`,
  decks: html`<svg viewBox="0 0 24 24" aria-hidden="true">${svg`<rect x="7" y="3.5" width="11" height="15" rx="1.8" fill="none" stroke="currentColor" stroke-width="1.8"></rect><path d="M5 7v12.2c0 .7.6 1.3 1.3 1.3H14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>`}</svg>`,
  profile: html`<svg viewBox="0 0 24 24" aria-hidden="true">${svg`<circle cx="12" cy="8.5" r="4" fill="none" stroke="currentColor" stroke-width="1.8"></circle><path d="M4.5 20.5c.8-4 3.8-6 7.5-6s6.7 2 7.5 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>`}</svg>`,
  admin: html`<svg viewBox="0 0 24 24" aria-hidden="true">${svg`<path d="M12 3.5 19 6v5.5c0 4.4-2.9 7.7-7 9-4.1-1.3-7-4.6-7-9V6l7-2.5Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path><path d="m9 12 2.2 2.2L15.5 10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>`}</svg>`,
  settings: html`<svg viewBox="0 0 24 24" aria-hidden="true">${svg`<circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"></path>`}</svg>`,
};

const REPORT_ICON = html`<svg viewBox="0 0 24 24" aria-hidden="true">${svg`<path d="M5.5 21V4.5m0 0h11.5l-2.3 4.25L17 13H5.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"></path>`}</svg>`;

export function renderTopNav(n: TopNav): TemplateResult {
  const tab = (name: TopNavTab, label: string) => html`<a data-tour=${name} class="tab ${n.open === name ? "on" : ""}" href=${n.links[name]}
    aria-current=${n.open === name ? "page" : nothing}>${ICONS[name]}<span>${label}</span></a>`;
  return html`<nav class="topnav" aria-label="Main">
    <a class="brand" href=${n.links.home}><img src="brand/logo.png" alt="On the Stack" width="400" height="103" /></a>
    ${tab("home", "Home")}
    ${tab("decks", "Decks")}
    ${tab("profile", "Profile")}
    ${n.admin ? tab("admin", "Admin") : nothing}
    <span class="spacer"></span>
    ${n.online != null ? html`<span class="online" title="Signed in and active in the last two minutes"><i aria-hidden="true"></i><b>${n.online}</b> online</span>` : nothing}
    ${n.displayName ? html`<span class="who">Signed in as <b>${n.displayName}</b></span>` : nothing}
    <button type="button" class="report-issue" aria-label="Report issue" @click=${(e: Event) => askToReportIssue(e.currentTarget!)}>${REPORT_ICON}<span>Report issue</span></button>
    <a data-tour="settings" class="tab settings-tab ${n.open === "settings" ? "on" : ""}" href=${n.links.settings} aria-label="Settings"
      aria-current=${n.open === "settings" ? "page" : nothing}>${ICONS.settings}</a>
  </nav>`;
}

// Each tab's page, relative to the release's base. The tabs swap in place (app.ts), so each path is
// also served the same page for a link opened fresh (justfile's cf-build).
const PAGE_PATHS: Record<TopNavTab, string> = { home: "./", decks: "deck-editor", profile: "profile", settings: "settings", admin: "admin" };

export function pageAt(url: URL): TopNavTab | null {
  const base = new URL(document.baseURI);
  if (url.origin !== base.origin) return null;
  const tabs = Object.keys(PAGE_PATHS) as TopNavTab[];
  return tabs.find((tab) => new URL(PAGE_PATHS[tab], base).pathname === url.pathname) ?? null;
}

// Where the tabs lead, keeping dev auth's ?user=.
export function topNavLinks(decksHref: string): Record<TopNavTab, string> {
  const params = new URLSearchParams();
  const { user } = readUrlParams();
  if (user) params.set("user", user);
  return { home: `${PAGE_PATHS.home}?${params}`, decks: decksHref, profile: `${PAGE_PATHS.profile}?${params}`, settings: `${PAGE_PATHS.settings}?${params}`, admin: `${PAGE_PATHS.admin}?${params}` };
}

export const topNavStyles = css`
  .topnav { box-sizing: border-box; display: flex; align-items: stretch; gap: 2px; height: 58px; padding: 0 24px; background: var(--bg); border-bottom: 1px solid var(--border); }
  .topnav .brand { display: flex; align-self: center; margin-right: 20px; }
  .topnav .brand img { width: auto; height: 26px; }
  .tab {
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; padding: 3px 16px 0;
    font-size: 12.5px; font-weight: 500; color: var(--text-dim); border-bottom: 3px solid transparent; text-decoration: none;
  }
  .tab svg { width: 22px; height: 22px; }
  .tab:hover { color: var(--text-strong); text-decoration: none; }
  .tab.on { color: var(--text-strong); border-bottom-color: #e67b3b; }
  .tab.settings-tab { padding: 3px 12px 0; }
  .topnav .spacer { flex: 1; }
  .topnav .who { align-self: center; margin-right: 8px; color: var(--text-dim); }
  .topnav .who b { color: var(--text-strong); font-weight: 600; }
  .topnav .online { display: flex; align-items: center; gap: 6px; align-self: center; margin-right: 18px; color: var(--text-dim); }
  .topnav .online i { width: 7px; height: 7px; border-radius: 50%; background: var(--good); box-shadow: 0 0 0 3px var(--good-soft); }
  .topnav .online b { color: var(--text-strong); font-weight: 600; font-variant-numeric: tabular-nums; }
  .topnav .report-issue {
    display: flex; align-items: center; gap: 6px; align-self: center; margin: 0 4px; padding: 6px 11px; font: inherit; font-size: 12.5px; font-weight: 500;
    color: var(--text-dim); background: none; border: 1px solid var(--border-strong); border-radius: 5px; transition: color 150ms, border-color 150ms;
  }
  .topnav .report-issue svg { width: 15px; height: 15px; }
  .topnav .report-issue:hover { color: var(--text-strong); border-color: var(--text-faint); }
  /* A phone: the Profile tab says who's signed in, and the tabs close up to fit. */
  @media (max-width: 600px) {
    .topnav { gap: 0; padding: 0 16px; }
    .topnav .brand { margin-right: 8px; }
    .topnav .brand img { height: 22px; }
    .tab { padding: 3px 10px 0; }
    .tab.settings-tab { padding: 3px 8px 0; }
    .topnav .who, .topnav .online { display: none; }
    .topnav .report-issue { padding: 6px 8px; }
    .topnav .report-issue span { display: none; }
  }
  @media (max-width: 360px) {
    .topnav .brand { display: none; }
  }
`;
